import { useEffect, useState } from "react";
import { Loader2, UserPlus, Copy, KeyRound, Trash2, ShieldCheck, ShieldOff, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function AdminTeam() {
  const { user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });
  const [tempPassword, setTempPassword] = useState(null); // {email, password}

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/users")
      .then((r) => { if (!cancelled) setAdmins(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    const r = await api.get("/admin/users");
    setAdmins(r.data || []);
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Email is required.");
    setInviting(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setTempPassword({ email: data.user.email, password: data.temporary_password });
      setForm({ email: "", name: "" });
      await reload();
      toast.success("Admin invited.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not invite."));
    } finally {
      setInviting(false);
    }
  };

  const toggleActive = async (a) => {
    const verb = a.is_active ? "deactivate" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${verb} ${a.email}?`)) return;
    try {
      await api.patch(`/admin/users/${a.id}`, { is_active: !a.is_active });
      await reload();
      toast.success(`${a.email} ${a.is_active ? "deactivated" : "reactivated"}.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Permanently delete ${a.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${a.id}`);
      await reload();
      toast.success(`${a.email} deleted.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const resetPassword = async (a) => {
    if (!window.confirm(`Issue a new temporary password for ${a.email}? They'll be forced to change it on next login.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${a.id}/reset-password`);
      setTempPassword({ email: data.user.email, password: data.temporary_password });
      await reload();
      toast.success("Temporary password issued.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const copyPassword = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword.password);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Could not copy — please select & copy manually.");
    }
  };

  return (
    <div data-testid="admin-team-page">
      <header className="mb-8">
        <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Security</div>
        <h1 className="font-display text-3xl md:text-4xl text-charcoal">Team</h1>
        <p className="text-charcoal/65 font-light mt-2">Admins who can sign into the dashboard.</p>
      </header>

      {/* Banner: temp password reveal */}
      {tempPassword ? (
        <div className="bg-amber-50 border border-amber-300 p-5 md:p-6 mb-6" data-testid="temp-password-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-display text-lg text-charcoal">Temporary password for {tempPassword.email}</div>
              <p className="text-sm text-charcoal/70 mt-1">
                Send it to them via WhatsApp or in person. You won&apos;t see it again — they&apos;ll be forced to set a new one on first login.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="font-mono text-base bg-white border border-amber-300 px-3 py-2 text-charcoal flex-1" data-testid="temp-password-value">
                  {tempPassword.password}
                </code>
                <button type="button" onClick={copyPassword} data-testid="temp-password-copy" className="inline-flex items-center gap-2 h-10 px-4 bg-charcoal text-ivory text-[11px] uppercase tracking-[0.2em] hover:bg-teal-600">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
              </div>
              <button type="button" onClick={() => setTempPassword(null)} className="text-[10px] uppercase tracking-[0.22em] text-charcoal/60 hover:text-teal-600 mt-3">
                I&apos;ve saved it — dismiss
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Invite form */}
      <section className="bg-white border border-beige p-6 md:p-8 mb-8 max-w-3xl" data-testid="invite-section">
        <div className="flex items-center gap-2 text-teal-600 mb-2">
          <UserPlus className="h-4 w-4" strokeWidth={1.5} />
          <span className="uppercase tracking-[0.22em] text-[11px] font-medium">Invite admin</span>
        </div>
        <h2 className="font-display text-xl text-charcoal mb-5">Add a teammate</h2>
        <form onSubmit={invite} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" data-testid="invite-form">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Email</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="teammate@example.com"
              data-testid="invite-email"
              className="mt-2 field-input"
            />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Name (optional)</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Romario Costa"
              data-testid="invite-name"
              className="mt-2 field-input"
            />
          </label>
          <div className="md:col-span-2">
            <button type="submit" disabled={inviting} data-testid="invite-submit" className="btn-primary h-11 px-6 disabled:opacity-60">
              {inviting ? <><Loader2 className="h-4 w-4 animate-spin" />Inviting</> : <><UserPlus className="h-4 w-4" />Send invite</>}
            </button>
            <p className="text-[11px] text-charcoal/45 mt-3">
              We&apos;ll generate a strong temporary password that you can hand over directly. Force-change is enabled.
            </p>
          </div>
        </form>
      </section>

      {/* Admin roster */}
      <section className="bg-white border border-beige overflow-hidden" data-testid="admin-roster">
        <div className="px-6 py-4 border-b border-beige flex items-center justify-between">
          <h2 className="font-display text-xl text-charcoal">Admins</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">{admins.length} total</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-charcoal/40">Loading&hellip;</div>
        ) : admins.length === 0 ? (
          <div className="p-10 text-center text-charcoal/40 italic">No admins.</div>
        ) : (
          <ul className="divide-y divide-beige">
            {admins.map((a) => {
              const isSelf = a.id === me?.id;
              return (
                <li key={a.id} className="px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`admin-row-${a.email}`}>
                  <div>
                    <div className="font-display text-lg text-charcoal">
                      {a.name || "Admin"}
                      {isSelf ? <span className="ml-2 px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] bg-teal-600 text-ivory">You</span> : null}
                    </div>
                    <div className="text-sm text-charcoal/65 flex items-center gap-1.5 mt-0.5">
                      <Mail className="h-3 w-3" /> {a.email}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.22em]">
                      <span className={`px-2 py-1 ${a.is_active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {a.is_active ? "Active" : "Deactivated"}
                      </span>
                      {a.must_change_password ? (
                        <span className="px-2 py-1 bg-amber-100 text-amber-800">Pending password change</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!isSelf ? (
                      <>
                        <button type="button" onClick={() => resetPassword(a)} data-testid={`reset-${a.email}`} className="inline-flex items-center gap-1.5 h-9 px-3 text-[10px] uppercase tracking-[0.22em] border border-beige text-charcoal hover:border-teal-600 hover:text-teal-600">
                          <KeyRound className="h-3 w-3" /> Reset password
                        </button>
                        <button type="button" onClick={() => toggleActive(a)} data-testid={`toggle-${a.email}`} className="inline-flex items-center gap-1.5 h-9 px-3 text-[10px] uppercase tracking-[0.22em] border border-beige text-charcoal hover:border-amber-600 hover:text-amber-700">
                          {a.is_active ? <><ShieldOff className="h-3 w-3" /> Deactivate</> : <><ShieldCheck className="h-3 w-3" /> Reactivate</>}
                        </button>
                        <button type="button" onClick={() => removeAdmin(a)} data-testid={`delete-${a.email}`} className="inline-flex items-center gap-1.5 h-9 px-3 text-[10px] uppercase tracking-[0.22em] border border-rose-300 text-rose-700 hover:bg-rose-700 hover:text-ivory">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      </>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/40">
                        Use Change Password to update your own account.
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
